import { useState } from "react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { MessageSquare, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { submitFeedback } from "../lib/api";

export default function FeedbackWidget({ context = {} }) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    if (rating === 0) {
      toast.error("Pick a rating first.");
      return;
    }
    setSubmitting(true);
    try {
      await submitFeedback({
        rating,
        comment: comment.trim(),
        email: email.trim(),
        audience: context.audience || "",
        industry: context.industry || "",
        health_score: context.health_score ?? null,
        rating_label: context.rating_label || "",
        page: "report",
      });
      toast.success("Thanks — we read every note.");
      setOpen(false);
      setRating(0);
      setComment("");
      setEmail("");
    } catch {
      toast.error("Could not send feedback. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="no-print fixed bottom-6 right-6 z-30 group flex items-center gap-2 bg-[#1F382E] text-[#F7F5F0] rounded-full pl-4 pr-5 py-3 shadow-lg hover:bg-[#142922] transition-all duration-300"
          data-testid="feedback-open-btn"
        >
          <MessageSquare size={16} strokeWidth={1.5} />
          <span className="text-sm">Give feedback</span>
        </button>
      </DialogTrigger>
      <DialogContent
        className="bg-white border-[#E5E0D8] sm:max-w-md"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl text-[#1F382E]">
            Help shape the next version.
          </DialogTitle>
          <DialogDescription className="text-[#5C665D]">
            We're in beta. Tell us what was useful, what was missing, what to fix.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-[#5C665D] mb-2">
              How useful was this report?
            </p>
            <div
              className="flex items-center gap-1.5"
              data-testid="feedback-rating"
              onMouseLeave={() => setHover(0)}
            >
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  data-testid={`feedback-star-${n}`}
                  className="p-1 transition-transform hover:scale-110"
                  aria-label={`${n} star${n > 1 ? "s" : ""}`}
                >
                  <Star
                    size={26}
                    strokeWidth={1.5}
                    className={
                      (hover || rating) >= n
                        ? "fill-[#C26D5C] text-[#C26D5C]"
                        : "text-[#D4C9B6]"
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-[#5C665D] mb-2">
              What did you think?
            </p>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Most useful part… most confusing part… one thing you'd add…"
              data-testid="feedback-comment"
              className="bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E] min-h-[100px]"
            />
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-[#5C665D] mb-2">
              Email (optional — only if you want a reply)
            </p>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.com"
              data-testid="feedback-email"
              className="bg-white border-[#E5E0D8] focus-visible:ring-[#1F382E]"
            />
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="feedback-submit-btn"
            className="bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0] rounded-md transition-all duration-300"
          >
            <Send size={14} strokeWidth={1.5} className="mr-2" />
            {submitting ? "Sending…" : "Send feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
